"use client";

import React from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { useLockBodyScroll } from "@/src/shared/hooks/useLockBodyScroll";

interface IInfoSheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}

/**
 * Bottom sheet on mobile, centred modal on md+ — the app's "Things to know" detail view.
 * Portaled to `document.body` above the page chrome (header, reserve bar, footer), and the
 * page behind doesn't scroll while it is open.
 */
const InfoSheet: React.FC<IInfoSheetProps> = ({
  open,
  onClose,
  title,
  children,
}) => {
  const titleId = React.useId();

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  useLockBodyScroll(open);

  if (typeof document === "undefined") return null;
  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[1000000] flex items-end justify-center md:items-center">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50"
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 240 }}
            className="relative flex max-h-[85vh] w-full max-w-xl flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl md:rounded-2xl"
          >
            <div className="flex justify-end px-5 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="rounded-full p-1 hover:bg-neutral-100"
                aria-label="Close"
              >
                <X className="h-6 w-6 text-ink-black" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto overscroll-contain px-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))]">
              <h3
                id={titleId}
                className="text-2xl font-semibold text-ink-black"
              >
                {title}
              </h3>
              {children}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default InfoSheet;
